import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import { SubRoutePartnersLayoutService } from '../../sub-route-partners-layout.service';
import { BPGroupedItem } from '../../types/bp-grouping.types';
import { GroupCardComponent } from '../group-card/group-card.component';
import {
  GroupPartnersModalComponent,
  GroupPartnersModalData,
} from '../group-partners-modal/group-partners-modal.component';
import { UnassignedPartnersPanelService } from '../unassigned-partners-panel.service';
import { UnassignedPartnersGroupedService } from './unassigned-partners-grouped.service';

@Component({
  selector: 'app-unassigned-partners-grouped',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, MatIconModule, GroupCardComponent, TranslatePipe],
  providers: [UnassignedPartnersGroupedService],
  templateUrl: './unassigned-partners-grouped.component.html',
  styles: [
    `
      :host {
        display: block;
        height: 100%;
      }

      .groups-list {
        &::-webkit-scrollbar {
          width: 6px;
        }

        &::-webkit-scrollbar-track {
          background: rgba(var(--fuse-bg-default-rgb), 1);
        }

        &::-webkit-scrollbar-thumb {
          background: rgba(var(--fuse-border-rgb), 0.4);
          border-radius: 3px;

          &:hover {
            background: rgba(var(--fuse-border-rgb), 0.6);
          }
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnassignedPartnersGroupedComponent {
  private groupedService = inject(UnassignedPartnersGroupedService);
  private panelService = inject(UnassignedPartnersPanelService);
  private layoutService = inject(SubRoutePartnersLayoutService);
  private dialog = inject(MatDialog);

  // === SIGNALS FROM SERVICE ===
  readonly data = this.groupedService.data;
  readonly loading = this.groupedService.loading;
  readonly loadingMore = this.groupedService.loadingMore;
  readonly hasMore = this.groupedService.hasMore;
  readonly groupingCriteria = this.groupedService.groupingCriteria;

  /**
   * Handle scroll event for infinite scroll.
   */
  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const threshold = 100;
    const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < threshold;

    if (atBottom && this.hasMore() && !this.loadingMore()) {
      this.groupedService.loadMore$.next();
    }
  }

  /**
   * Open modal to show partners of a group.
   */
  openGroupDetailModal(group: BPGroupedItem): void {
    const subRouteId = this.panelService.subRouteIdForGroupView();
    if (!subRouteId) return;

    const data: GroupPartnersModalData = {
      group,
      subRouteId,
      groupingCriteria: this.groupingCriteria(),
      companyId: this.panelService.subRouteData()?.company?._id?.toString() ?? null,
    };

    const dialogRef = this.dialog.open(GroupPartnersModalComponent, {
      data,
      width: '500px',
      maxHeight: '80vh',
    });

    dialogRef.afterClosed().subscribe((hasChanges: boolean) => {
      if (hasChanges) {
        this.groupedService.reload$.next();
        this.layoutService.refreshAssignedPartners$.next();
      }
    });
  }
}
